import { CommonModule } from "@angular/common";
import { Component, DestroyRef, computed, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

// EZ
import { DocMeta } from "@template-ui/decorators";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormDialogService } from "@template-ui/services";
import { EzFormProgressDialogEvent } from "@template-ui/types/ez-form-dialog.types";
import {
  ButtonClickEvent,
  EzFormDialogMode,
  EzFormInputType,
  EzFormProgressInitiateDialogButtonConfig,
  EzFormProgressDialogCompletion,
  EzFormProgressDialogRequest,
  FormValuesObject,
  InputConfig,
} from "@template-ui/types";

interface ProgressSampleFormValues extends FormValuesObject {
  simulateErrorResult: boolean;
  useCallbackMode: boolean;
}

interface AddPersonFormValues extends FormValuesObject {
  firstName: string;
  lastName: string;
}

@DocMeta({
  hide: true,
  showcaseTitle: "Progress Dialog Flow Sample",
  showcaseDesc: "Demonstrates EzFormDialogService openProgress() and openProgress$() flow."
})
@Component({
  selector: "ui-form-progress-dialog-sample, ez-form-progress-dialog-sample",
  imports: [CommonModule, FormsModule, UiLibraryModule],
  templateUrl: "./ez-form-progress-dialog-sample.component.html",
  styleUrl: "./ez-form-progress-dialog-sample.component.scss"
})
/**
 * Showcase component demonstrating the progress dialog flow APIs.
 */
export class EzFormProgressDialogSampleComponent {
  // ---------------------------------------------------------------------------
  //  Dependency Injection
  // ---------------------------------------------------------------------------
  /** Service used to open progress dialog flows. */
  private readonly ezFormDialog = inject(EzFormDialogService);
  /** Lifecycle handle for observable teardown. */
  private readonly destroyRef = inject(DestroyRef);

  // ---------------------------------------------------------------------------
  //  Public Template-Bound Properties
  // ---------------------------------------------------------------------------
  /** Stores the final completion payload from the most recent run. */
  protected readonly lastCompletion = signal<EzFormProgressDialogCompletion<string, string> | null>(null);
  /** Indicates an active progress flow is running. */
  protected readonly isRunning = signal<boolean>(false);
  /** Form values for the sample controls. */
  protected readonly sampleFormValuesSig = signal<ProgressSampleFormValues>({
    simulateErrorResult: false,
    useCallbackMode: false,
  });

  /** True when the sample should force an error result. */
  protected readonly useErrorPath = computed<boolean>(() => Boolean(this.sampleFormValuesSig().simulateErrorResult));
  /** True when processing should complete via controller callback. */
  protected readonly useCallbackMode = computed<boolean>(() => Boolean(this.sampleFormValuesSig().useCallbackMode));

  /** Form config that contains sample inputs and run buttons. */
  protected readonly sampleFormConfig: InputConfig[] = [
    { type: EzFormInputType.CHECKBOX, name: "simulateErrorResult", label: "Simulate error result", row: 1, width: "2x" },
    { type: EzFormInputType.CHECKBOX, name: "useCallbackMode", label: "Use callback completion (instead of returning Promise)", row: 2, width: "2x" },
    { type: EzFormInputType.BUTTON, name: "runPromise", buttonText: "Add Person with openProgress()", row: 3, disabled: () => this.isRunning() },
    { type: EzFormInputType.BUTTON, name: "runObservable", buttonText: "Add Person with openProgress$()", row: 3, disabled: () => this.isRunning() },
  ];

  /** Computed progress request from current sample settings. */
  protected readonly progressRequest = computed<EzFormProgressDialogRequest<AddPersonFormValues, string, string>>(() => {
    const initiateButtons: EzFormProgressInitiateDialogButtonConfig[] = [
      { id: "yes", buttonText: "Yes", startsProgressFlow: true },
      { id: "cancel", buttonText: "No", startsProgressFlow: false },
    ];

    const initiateInputs: InputConfig[] = [
      { type: EzFormInputType.TEXT, name: "firstName", label: "First Name", defaultValue: "Avery" },
      { type: EzFormInputType.TEXT, name: "lastName", label: "Last Name", defaultValue: "Johnson" },
    ];

    return {
      initiateDialog: {
        mode: EzFormDialogMode.CALLBACK,
        header: "Add Person?",
        message: "Do you want to add this person?",
        formInputConfigs: initiateInputs,
        footerButtons: initiateButtons,
        width: "36rem",
      },
      processingDialog: {
        html: "Creating <b>{{firstName}} {{lastName}}</b>...",
        width: "30rem",
      },
      successDialog: {
        html: "Person <b>{{firstName}} {{lastName}}</b> created. Result: <b>{{result}}</b>",
        width: "40rem",
      },
      errorDialog: {
        html: "Failed to create <b>{{firstName}} {{lastName}}</b>. Error: <b>{{error}}</b>",
        width: "40rem",
      },
      width: "36rem",
      onStartProcessing: (_, controller) => {
        if (this.useCallbackMode()) {
          setTimeout(() => {
            if (this.useErrorPath()) {
              controller.completeError("Callback mode failed.");
              return;
            }
            controller.completeSuccess(`Person #${Math.floor(Math.random() * 9000) + 1000}`);
          }, 1200);
          return;
        }

        return new Promise<string>((resolve, reject) => {
          setTimeout(() => {
            if (this.useErrorPath()) {
              reject("Promise mode failed.");
              return;
            }
            resolve(`Person #${Math.floor(Math.random() * 9000) + 1000}`);
          }, 1200);
        });
      }
    };
  });

  // ---------------------------------------------------------------------------
  //  Public Methods
  // ---------------------------------------------------------------------------
  /** Starts the sample flow using the promise-returning API. */
  protected onRunPromise(): void {
    this.isRunning.set(true);
    this.lastCompletion.set(null);

    void this.ezFormDialog.openProgress(this.progressRequest()).then((completion) => {
      this.lastCompletion.set(completion ?? null);
      this.isRunning.set(false);
    });
  }

  /** Starts the sample flow using the observable event API. */
  protected onRunObservable(): void {
    this.isRunning.set(true);
    this.lastCompletion.set(null);

    this.ezFormDialog.openProgress$(this.progressRequest())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.type !== EzFormProgressDialogEvent.COMPLETE) {
          return;
        }

        this.lastCompletion.set(event.payload);
        this.isRunning.set(false);
      });
  }

  /** Handles sample form button clicks. */
  protected onSampleButtonClick(event: ButtonClickEvent): void {
    switch (event.name) {
      case "runPromise":
        this.onRunPromise();
        break;
      case "runObservable":
        this.onRunObservable();
        break;
      default:
        break;
    }
  }
}
